import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Col,
  Drawer,
  Row,
  Select,
  message,
} from 'antd';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { DeleteOutlined } from '@ant-design/icons';
import * as Sentry from '@sentry/react';
import axios from 'axios';

import { toTitleCase } from '../helpers/helpers';
import DrawerSubmitFooter from '../common/containers/DrawerSubmitFooter';
import BorderlessButton from '../common/buttons/BorderlessButton';
import OnTraccrTextInput from '../common/inputs/OnTraccrTextInput';

const LOCAL_STORAGE_KEY = 'boardDeepSearchFields';

const setLocalStorage = (value, cardTypeId) => {
  const currentValue = window.localStorage.getItem(LOCAL_STORAGE_KEY);
  let currentValueObj = {};
  try {
    if (currentValue) currentValueObj = JSON.parse(currentValue);
  } catch (err) {
    //
  }
  currentValueObj[cardTypeId] = value;
  window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(currentValueObj));
};

export default function BoardDeepSearchDrawer({
  visible,
  onClose,
  selectedBoard,
  setDeepSearchCards,
  setDeepSearchText,
}) {
  const {
    cardTypeId,
  } = selectedBoard ?? {};

  const cardTemplates = useSelector((state) => state.boards.cardTemplates);

  const [addedFields, setAddedFields] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    cardTypeTitle,
    relevantTextFields,
  } = useMemo(() => {
    const relevantFields = [];
    const cardType = cardTemplates[cardTypeId];
    const {
      title,
      fields: sections,
    } = cardType ?? {};

    sections?.forEach((section) => {
      const { fields, name: sectionName } = section ?? {};
      fields?.forEach((field) => {
        const {
          configProps: {
            title: fieldTitle,
          },
          id,
          selectedType,
        } = field ?? {};
        if (selectedType === 'text') {
          relevantFields.push({
            value: id,
            label: `${sectionName} - ${fieldTitle}`,
          });
        }
      });
    });

    return {
      cardTypeTitle: title ?? '',
      relevantTextFields: relevantFields,
    };
  }, [cardTemplates, cardTypeId]);

  const filteredTextFields = useMemo(() => {
    const addedFieldSet = new Set(addedFields.map((field) => field.value));
    return relevantTextFields.filter((field) => !addedFieldSet.has(field.value));
  }, [relevantTextFields, addedFields]);

  const onTextFieldChange = useCallback((value, textInput) => {
    const newAddedFields = addedFields.map((field) => {
      if (field.value === value) {
        return { ...field, textInput };
      }
      return field;
    });
    setAddedFields(newAddedFields);
  }, [addedFields]);

  const onTextFieldAdd = useCallback((_, value) => {
    const newAddedFields = [...addedFields, value];
    setAddedFields(newAddedFields);
    setLocalStorage(newAddedFields, cardTypeId);
  }, [addedFields, cardTypeId]);

  const onTextFieldDelete = useCallback((value) => {
    const newAddedFields = addedFields.filter((field) => field?.value !== value);
    setAddedFields(newAddedFields);
    setLocalStorage(newAddedFields, cardTypeId);
  }, [addedFields, cardTypeId]);

  const fieldWithText = useMemo(() => {
    const foundField = addedFields.find((field) => !!field?.textInput);
    return foundField ?? null;
  }, [addedFields]);

  const onSearch = useCallback(async () => {
    if (!fieldWithText || !cardTypeId) return;
    setLoading(true);
    try {
      const { data: newData = [] } = await axios.get('/boards/deepSearch', {
        params: {
          cardTypeId,
          fieldId: fieldWithText.value,
          text: fieldWithText.textInput,
        },
      });
      setDeepSearchCards(newData);
      setDeepSearchText(fieldWithText.textInput);
    } catch (err) {
      Sentry.withScope(() => {
        Sentry.captureException(err);
      });
      message.error('Failed to search for cards');
    }
    onClose();
    setLoading(false);
  }, [cardTypeId, fieldWithText, onClose]);

  useEffect(() => {
    if (!visible) {
      setAddedFields([]);
      setLoading(false);
    }
  }, [visible]);

  useEffect(() => {
    if (visible && cardTypeId) {
      const rawAddedFields = window.localStorage.getItem(LOCAL_STORAGE_KEY);
      if (rawAddedFields) {
        setAddedFields(JSON.parse(rawAddedFields)[cardTypeId] ?? []);
      }
    }
  }, [visible, cardTypeId]);

  return (
    <Drawer
      title={`Deep Search for ${toTitleCase(cardTypeTitle)}`}
      visible={visible}
      onClose={onClose}
      width={1100}
      destroyOnClose
    >
      <Row>
        <Col>
          Select Text Field to Search:
        </Col>
      </Row>
      <Row style={{ marginTop: 10, marginBottom: 20 }}>
        <Col style={{ width: '100%' }}>
          <Select
            options={filteredTextFields}
            onChange={onTextFieldAdd}
            optionFilterProp="label"
            showSearch
            style={{ width: '100%' }}
            value={null}
          />
        </Col>
      </Row>
      {addedFields.map((field, index) => (
        <div style={{ marginBottom: 10, border: '1px solid #ccc', padding: 10 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <b>{`${index + 1} - ${field.label}`}</b>
            </Col>
            <Col>
              <BorderlessButton
                iconNode={<DeleteOutlined style={{ margin: 0, color: 'red' }} />}
                onClick={() => onTextFieldDelete(field.value)}
              />
            </Col>
          </Row>
          <Row style={{ marginTop: 5 }}>
            <OnTraccrTextInput
              value={field?.textInput ?? ''}
              onChange={(e) => onTextFieldChange(field.value, e?.target?.value)}
              disabled={fieldWithText && fieldWithText.value !== field.value}
            />
          </Row>
        </div>
      ))}
      <DrawerSubmitFooter
        onClose={onClose}
        onSubmit={onSearch}
        loading={loading}
        canSubmit={!!fieldWithText}
        submitTitle="Search"
      />
    </Drawer>
  );
}

BoardDeepSearchDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedBoard: PropTypes.shape({}),
  setDeepSearchCards: PropTypes.func.isRequired,
  setDeepSearchText: PropTypes.func.isRequired,
};

BoardDeepSearchDrawer.defaultProps = {
  selectedBoard: {},
};
